import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/userApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Send, KeyRound, ArrowLeft, Eye, EyeOff, Fingerprint } from "lucide-react";
import { CircuitBg } from "@/components/CircuitBg";
import { GlassField, GlassButton, GlassDivider } from "@/components/GlassBits";
import { loginWithPasskey, passkeysSupported, passkeyError } from "@/lib/passkeys";
import { stormSurge } from "@/lib/pulse";

/** Acceso exclusivo para administradores (separado del acceso de usuarios). */
export default function AdminLogin() {
  const [adminCode, setAdminCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [telegramId, setTelegramId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [tgFallback, setTgFallback] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const codeLogin = async () => {
    if (adminCode.trim().length < 8) return toast.error("El código de administrador tiene al menos 8 caracteres");
    setBusy("admincode");
    stormSurge();
    try {
      const { data } = await api.post("/admin/auth/code-login", { code: adminCode.trim() });
      login(data.user);
      toast.success(`Bienvenido ${data.user.name || data.user.telegram_id}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e, "Código incorrecto"));
    } finally { setBusy(null); }
  };

  const passkeyLogin = async () => {
    setBusy("passkey");
    stormSurge();
    try {
      const data = await loginWithPasskey();
      login(data.user);
      toast.success(`Bienvenido ${data.user.name || data.user.telegram_id}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(passkeyError(e, "No se pudo entrar con huella"));
    } finally { setBusy(null); }
  };

  const requestCode = async () => {
    if (!telegramId.trim()) return toast.error("Ingresa tu Telegram ID");
    setBusy("code");
    stormSurge();
    try {
      await api.post("/admin/auth/request-code", { telegram_id: telegramId.trim() });
      toast.success("Código enviado por Telegram");
      setCooldown(30);
    } catch (e) {
      toast.error(apiError(e, "No se pudo enviar el código"));
    } finally { setBusy(null); }
  };

  const verify = async () => {
    if (code.trim().length !== 6) return toast.error("El código tiene 6 dígitos");
    setBusy("verify");
    try {
      const { data } = await api.post("/admin/auth/verify", {
        telegram_id: telegramId.trim(), code: code.trim(),
      });
      login(data.user);
      toast.success("Bienvenido");
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e, "Código incorrecto"));
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center circuit-bg theme-noir py-14" style={{ backgroundColor: "#05070C" }}>
      <CircuitBg />
      <div className="circuit-veil" aria-hidden="true" />
      <div className="w-full max-w-md px-6 animate-fade-up">
        <div className="flex flex-col items-center mb-7" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.9)" }}>
          <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
            className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border border-white/10"
            style={{ boxShadow: "0 0 46px rgba(255,138,43,0.35)" }} />
          <div className="font-heading text-[11px] tracking-[0.34em] text-amber-300/80 mb-1.5">DIGITAL FORGE</div>
          <h1 className="font-heading text-2xl font-bold text-white">Acceso de Administradores</h1>
          <p className="text-sm text-white/70 mt-1 tracking-[0.18em]">ÁREA RESTRINGIDA</p>
        </div>

        <div className="glass-card p-7" data-testid="admin-access-card">
          <div className="relative">
            <GlassField data-testid="admin-code-input" label="Código de administrador"
              type={showCode ? "text" : "password"} value={adminCode} autoComplete="off"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              inputClass="font-mono tracking-[0.2em] pr-11" placeholder="Tu código único"
              onChange={(e) => setAdminCode(e.target.value.trim())}
              onKeyDown={(e) => e.key === "Enter" && codeLogin()} />
            <button type="button" data-testid="admin-code-reveal"
              aria-label={showCode ? "Ocultar el código" : "Ver el código"}
              onClick={() => setShowCode((v) => !v)}
              className="absolute right-3 bottom-2.5 text-white/50 hover:text-red-300 transition-colors">
              {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="mt-5">
            <GlassButton data-testid="admin-code-btn" tone="red" icon={ShieldCheck}
              loading={busy === "admincode"} onClick={codeLogin}>
              Entrar al panel
            </GlassButton>
          </div>

          {passkeysSupported() && (
            <>
              <GlassDivider>o sin teclear nada</GlassDivider>
              <GlassButton data-testid="admin-passkey-btn" tone="neutral" icon={Fingerprint}
                loading={busy === "passkey"} onClick={passkeyLogin}>
                Entrar con huella o cara
              </GlassButton>
              <p className="text-[11px] text-white/40 mt-2">
                Primero registra este dispositivo dentro del panel, en Administradores.
              </p>
            </>
          )}

          <p className="text-[11px] text-white/40 mt-4">
            Cada administrador tiene un código único y fijo que entrega el SUPER_ADMIN.
            Si no tienes uno, pide acceso por Telegram.
          </p>

          <button type="button" data-testid="admin-telegram-toggle"
            onClick={() => setTgFallback((v) => !v)}
            className="w-full text-[11px] text-white/45 hover:text-white/75 transition-colors mt-4">
            {tgFallback ? "Ocultar acceso por Telegram" : "Entrar con un código temporal de Telegram"}
          </button>

          <div className={tgFallback ? "block" : "hidden"} data-testid="admin-telegram-box">
            <GlassDivider>código temporal</GlassDivider>
            <div className="space-y-3">
              <GlassField data-testid="login-telegram-id-input" label="Tu Telegram ID" value={telegramId}
                inputClass="font-mono" placeholder="Ej: 6731591552"
                onChange={(e) => setTelegramId(e.target.value.replace(/\D/g, ""))} />
              <div className="flex items-end gap-2">
                <GlassField data-testid="login-code-input" className="flex-1" label="Código de 6 dígitos"
                  value={code} maxLength={6} inputClass="font-mono tracking-[0.35em] text-center"
                  placeholder="000000"
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && verify()} />
                <GlassButton data-testid="login-request-code-btn" tone="neutral" icon={Send}
                  className="w-auto whitespace-nowrap px-3.5" loading={busy === "code"}
                  disabled={cooldown > 0} onClick={requestCode}>
                  {cooldown > 0 ? `Reenviar en ${cooldown}s` : "Solicitar código"}
                </GlassButton>
              </div>
              <p className="text-[11px] text-white/40">
                Debes haber iniciado el bot con /start para recibir el código.
              </p>
              <GlassButton data-testid="login-verify-btn" tone="red" icon={KeyRound}
                loading={busy === "verify"} onClick={verify}>Verificar y entrar</GlassButton>
            </div>
          </div>
        </div>

        <Link to="/acceso" data-testid="go-user-access-link"
          className="mt-6 flex items-center justify-center gap-2 text-xs text-white/50 hover:text-white/85 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Acceso de usuarios
        </Link>
      </div>
    </div>
  );
}
